/*
 * CHATMESSAGES COMPONENT
 *
 * RENDERS A LIST OF CHAT BUBBLES FOR USER AND AI MESSAGES, PLUS A TYPING INDICATOR WHEN isTyping IS TRUE.
 * SUPPORTS MARKDOWN RENDERING WITH CUSTOM RENDERERS TO:
 *   1) FORCE LINKS ONTO THEIR OWN LINE (via SCSS)
 *   2) SILENTLY DROP PARAGRAPHS CONSISTING ONLY OF A SINGLE DOT
 *   3) DROP LIST ITEMS CONSISTING ONLY OF A SINGLE DOT (so no stray bullet)
 *   4) DROP ENTIRE LISTS WHEN ALL ITEMS ARE EMPTY
 *   5) HANDLE MARKDOWN RENDER ERRORS GRACEFULLY BY FALLING BACK TO PLAIN TEXT
 *
 * By default, when your AI response contains something like:
 *   … här:
 *   Städakademin
 *   .
 *   Där kan du läsa mer …
 *
 * Markdown turns that lone `.` into its own `<p>.</p>`—and even into an empty list item,
 * which then renders a bullet with no content.  With our custom renderers we drop:
 *  - any `<p>` whose AST‐text is exactly `.`
 *  - any `<li>` whose AST‐text is exactly `.` or empty
 *  - any `<ul>` or `<ol>` whose all child `<li>`s are dropped
 *  - **AND** we pre-clean the raw string to remove any lines that are exactly `.`
 *
 * NEW FEATURE:
 * - Now renders course recommendations immediately after the assistant message that contains them
 * - Recommendations appear in the message flow rather than at the bottom
 * - Maintains all existing markdown and email input functionality
 */

import React from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import styles from "./ChatMessages.module.scss";
import TypingIndicator from "../TypingIndicator/TypingIndicator";
import EmailInput from "./EmailInput/EmailInput";
import CourseRecommendation from "../Course_recomendation/CourseRecommendation";

/**
 * Error boundary around ReactMarkdown so a bad markdown parse
 * won't crash the chat UI. Falls back to plain string.
 */
class MarkdownErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("Markdown render error:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return <div className={styles.markdown}>{this.props.fallback}</div>;
    }
    return this.props.children;
  }
}
MarkdownErrorBoundary.propTypes = {
  fallback: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const ChatMessages = ({
  messages,
  isTyping,
  onEmailSubmit,
  recommendations,
  recommendationSourceId,
  viewport,
  host,
}) => {
  // 1) Sanitize incoming messages array
  const safeMessages = Array.isArray(messages)
    ? messages.filter(
      (m) =>
        m &&
        typeof m.content === "string" &&
        (m.role === "assistant" ||
          m.role === "user" ||
          m.type === "emailRequest") &&
        m.type !== "hide",
    )
    : [];

  // 2) Pre-clean: drop any raw line that's exactly "."
  const cleanContent = (raw) =>
    raw
      .split(/\r?\n/)
      .filter((line) => line.trim() !== ".")
      .join("\n");

  const shouldShowRecsAfterMessage = (index) => {
    const msg = safeMessages[index];
    return (
      recommendations.length > 0 &&
      msg.role === "assistant" &&
      msg.id === recommendationSourceId
    );
  };

  return (
    <div className={styles.chatMessagesContainer}>
      {safeMessages.map((msg, idx) => {
        // ---- Render an EmailInput when AI requests an email ----
        if (msg.type === "emailRequest") {
          return (
            <React.Fragment key={idx}>
              <div className={styles.aiMessage}>
                <div className={styles.messageBubble}>
                  <MarkdownErrorBoundary fallback={msg.content}>
                    <ReactMarkdown className={styles.markdown}>
                      {msg.content}
                    </ReactMarkdown>
                  </MarkdownErrorBoundary>
                  <EmailInput
                    placeholder="name@domain.com"
                    onSubmit={(address) => onEmailSubmit(address)}
                  />
                  {/* ↓ ny länk under e-postrutan ↓ */}
                  <div className={styles.privacyLink}>
                    <a
                      href="https://borago.se/policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Läs här om hur vi behandlar personuppgifter
                    </a>
                  </div>
                </div>
                <div className={styles.profile}>
                  <img
                    src="/assets/ai-borago-profile.svg"
                    alt="AI Profile"
                    className={styles.profileIcon}
                  />
                </div>
              </div>
            </React.Fragment>
          );
        }

        // ---- Normal message bubble ----
        const content = cleanContent(msg.content.trim());

        return (
          <React.Fragment key={idx}>
            <div
              className={
                msg.role === "assistant" ? styles.aiMessage : styles.userMessage
              }
            >
              <div className={styles.messageBubble}>
                <MarkdownErrorBoundary fallback={content}>
                  <ReactMarkdown
                    className={styles.markdown}
                    components={{
                      a: ({ children, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                          {children || "Link"}
                        </a>
                      ),
                    }}
                  >
                    {content.trim().replace(/\\n/g, "\n")}
                  </ReactMarkdown>
                </MarkdownErrorBoundary>
              </div>

              {msg.role === "assistant" && msg.type !== "emailRequest" && (
                <div className={styles.profile}>
                  <img
                    src="/assets/ai-borago-profile.svg"
                    alt="AI Profile"
                    className={styles.profileIcon}
                  />
                </div>
              )}
            </div>
            {/* Show recommendations immediately after the assistant message that generated them */}
            {shouldShowRecsAfterMessage(idx) && (
              <div className={styles.inlineRecs}>
                {recommendations.map((rec, i) => {
                  const isLast = i === recommendations.length - 1;
                  const isOdd = recommendations.length % 2 === 1;
                  const className = isLast && isOdd ? styles.centerIfOdd : "";

                  return (
                    <CourseRecommendation
                      key={rec.courseId || i}
                      data={rec}
                      compact
                      host={host}
                      className={className}
                    />
                  );
                })}
              </div>
            )}
          </React.Fragment>
        );
      })}

      {/* Typing indicator when AI is responding */}
      {isTyping && (
        <div className={styles.aiMessage}>
          <div className={styles.messageBubble}>
            <TypingIndicator />
          </div>
          <div className={styles.profile}>
            <img
              src="/assets/ai-borago-profile.svg"
              alt="AI Profile"
              className={styles.profileIcon}
            />
          </div>
        </div>
      )}
    </div>
  );
};

ChatMessages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(["assistant", "user"]).isRequired,
      type: PropTypes.string,
      content: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isTyping: PropTypes.bool.isRequired,
  onEmailSubmit: PropTypes.func.isRequired,
  recommendations: PropTypes.array,
  viewport: PropTypes.string,
  host: PropTypes.string,
};

ChatMessages.defaultProps = {
  recommendations: [],
  viewport: "desktop",
  host: "",
};

export default ChatMessages;

